import { FirebaseApp } from 'firebase/app';
import { Auth } from 'firebase/auth';
import {
  Firestore,
  writeBatch,
  WriteBatch,
  runTransaction,
  Transaction
} from 'firebase/firestore';
import { FirebaseStorage } from 'firebase/storage';
import { Functions } from 'firebase/functions';
// Import pre-initialized Firebase instances from frontend
import { app as frontendApp, db as frontendDb, auth as frontendAuth, storage as frontendStorage, functions as frontendFunctions } from '@frontend/firebase';

/**
 * CoreFirebaseService
 *
 * Singleton service that provides access to Firebase instances.
 * Implements centralized Firebase initialization and instance management.
 *
 * Features:
 * - Singleton pattern for consistent Firebase instance access
 * - Firestore, Storage, Auth, and Functions instance accessors
 * - Batch operations support
 * - Transaction operations support
 * - Error logging for Firebase operations
 *
 * Usage:
 * ```typescript
 * const core = CoreFirebaseService.getInstance();
 * const db = core.firestore;
 * const batch = core.batch();
 * await core.runTransaction(async (txn) => { ... });
 * ```
 */
export class CoreFirebaseService {
  private static instance: CoreFirebaseService | null = null;

  private app: FirebaseApp;
  private _firestore: Firestore;
  private _storage: FirebaseStorage;
  private _auth: Auth;
  private _functions: Functions;

  /**
   * Private constructor to enforce singleton pattern.
   * Initializes Firebase app and all service instances.
   */
  private constructor() {
    // Use pre-initialized Firebase instances from frontend/src/firebase.js
    // This ensures Firebase is initialized before CoreFirebaseService is created
    console.log('[CoreFirebaseService] Constructor called');
    console.log('[CoreFirebaseService] frontendApp:', frontendApp);
    console.log('[CoreFirebaseService] frontendDb:', frontendDb);
    console.log('[CoreFirebaseService] frontendAuth:', frontendAuth);
    console.log('[CoreFirebaseService] frontendStorage:', frontendStorage);
    console.log('[CoreFirebaseService] frontendFunctions:', frontendFunctions);

    this.app = frontendApp;
    this._firestore = frontendDb;
    this._storage = frontendStorage;
    this._auth = frontendAuth;
    this._functions = frontendFunctions;

    console.log('[CoreFirebaseService] After assignment - this._firestore:', this._firestore);
  }

  /**
   * Get the singleton instance of CoreFirebaseService.
   * Creates the instance on first call.
   *
   * @returns {CoreFirebaseService} The singleton instance
   */
  public static getInstance(): CoreFirebaseService {
    if (!CoreFirebaseService.instance) {
      CoreFirebaseService.instance = new CoreFirebaseService();
    }
    return CoreFirebaseService.instance;
  }

  /**
   * Get the Firestore instance.
   *
   * @returns {Firestore} The Firestore instance
   * @throws {Error} If Firestore is not initialized
   */
  public get firestore(): Firestore {
    if (!this._firestore) {
      throw new Error(
        '[CoreFirebaseService] Firestore is not initialized. ' +
        'Make sure Firebase is properly initialized in frontend/src/firebase.js before using services.'
      );
    }
    return this._firestore;
  }

  /**
   * Get the Storage instance.
   *
   * @returns {FirebaseStorage} The Storage instance
   */
  public get storage(): FirebaseStorage {
    return this._storage;
  }

  /**
   * Get the Auth instance.
   *
   * @returns {Auth} The Auth instance
   */
  public get auth(): Auth {
    return this._auth;
  }

  /**
   * Get the Functions instance.
   *
   * @returns {Functions} The Functions instance
   */
  public get functions(): Functions {
    return this._functions;
  }

  /**
   * Create a new Firestore batch.
   *
   * Batches allow you to execute multiple write operations as a single atomic unit.
   * All operations in a batch either succeed or fail together.
   *
   * @returns {WriteBatch} A new Firestore batch
   *
   * @example
   * ```typescript
   * const batch = core.batch();
   * batch.set(docRef1, data1);
   * batch.update(docRef2, data2);
   * batch.delete(docRef3);
   * await batch.commit();
   * ```
   */
  public batch(): WriteBatch {
    return writeBatch(this._firestore);
  }

  /**
   * Run a Firestore transaction.
   *
   * Transactions are a way to ensure data consistency when reading and writing data.
   * They automatically retry on contention and roll back on error.
   *
   * @param updateFunction - The function to run within the transaction
   * @returns {Promise<T>} The result of the transaction
   *
   * @example
   * ```typescript
   * await core.runTransaction(async (transaction) => {
   *   const doc = await transaction.get(docRef);
   *   const newValue = doc.data().value + 1;
   *   transaction.update(docRef, { value: newValue });
   * });
   * ```
   */
  public async runTransaction<T>(
    updateFunction: (transaction: Transaction) => Promise<T>
  ): Promise<T> {
    return runTransaction(this._firestore, updateFunction);
  }

  /**
   * Log Firebase errors with context.
   *
   * Provides centralized error logging for Firebase operations.
   * Logs error details including code, message, and context.
   *
   * @param error - The error to log
   * @param context - Additional context about where the error occurred
   */
  public logError(error: any, context: string): void {
    console.error(`[Firebase Error - ${context}]`, {
      code: error.code,
      message: error.message,
      details: error,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get current authenticated user ID.
   *
   * Returns the UID of the currently authenticated user, or null if no user is authenticated.
   * Useful for including user context in API requests, logging, and analytics.
   *
   * @returns {string | null} User ID (UID) or null if not authenticated
   *
   * @example
   * ```typescript
   * const core = CoreFirebaseService.getInstance();
   * const userId = core.getCurrentUserId();
   * if (userId) {
   *   console.log('Current user:', userId);
   * } else {
   *   console.log('No user authenticated');
   * }
   * ```
   */
  public getCurrentUserId(): string | null {
    return this._auth.currentUser?.uid || null;
  }

  /**
   * Get current environment name.
   *
   * Returns the environment name from VITE_NODE_ENV environment variable.
   * Used for environment-specific behavior and API requests.
   *
   * Possible values:
   * - 'development' - Local development (.env.dev)
   * - 'staging' - Staging environment (.env.stage)
   * - 'production' - Production environment (.env.prod)
   *
   * @returns {string} Environment name (defaults to 'development')
   *
   * @example
   * ```typescript
   * const core = CoreFirebaseService.getInstance();
   * const env = core.getEnvironment();
   * console.log('Current environment:', env); // 'development', 'staging', or 'production'
   * ```
   */
  public getEnvironment(): string {
    // Safe across runtimes: Vite replaces `process.env.NODE_ENV` at build
    // time; jest / Node populate it natively. The previous implementation
    // used `import.meta.env.VITE_NODE_ENV`, which jest's ts-jest parser
    // cannot handle (ESM-only syntax in a CommonJS context) and poisoned
    // every barrel-transitive consumer under test.
    return process.env.NODE_ENV || 'development';
  }
}
